import {
  combineReducers,
  configureStore,
  createListenerMiddleware,
} from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage

import { api } from './services/api';
import userReducer from './slices/user';
import botReducer from './slices/bot';
import overviewReducer from './slices/overview';
import subscriptionReducer from './slices/subscription';
import uiReducer from './slices/ui';

import startUserListeners from './listeners/user';
import startSubscriptionListeners from './listeners/subscription';

const persistConfig = {
  key: 'root',
  storage,
  blacklist: ['api', 'bot'],
};

export const listenerMiddleware = createListenerMiddleware();

listenerMiddleware.startListening({
  type: 'persist/REHYDRATE',
  effect: async (_, listenerApi) => {
    const userId = (listenerApi.getState() as RootState).user.id;

    if (userId) {
      listenerApi.dispatch(
        api.endpoints.getSubscriptionSummariesByUserId.initiate(userId),
      );
    }

    // Fetch list of bot ids (if master or dev)
    // @TODO, We want to set more than just userId
    // So we can check on master of dev, not the userId
    // if (userId === 1 || userId === 2) {
    if (userId === 1) {
      listenerApi.dispatch(api.endpoints.getBotIds.initiate(undefined));
    }
  },
});

const rootReducer = combineReducers({
  user: userReducer,
  overview: overviewReducer,
  bot: botReducer,
  subscription: subscriptionReducer,
  ui: uiReducer,
  [api.reducerPath]: api.reducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    })
      .prepend(api.middleware)
      .concat(listenerMiddleware.middleware),
});

export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

startUserListeners();
startSubscriptionListeners();
